import { trackAdsConversion, trackAnalyticsEvent } from "@/utils/analytics";

// Auth events
export const trackLogin = (params?: { method?: string; role?: string }) =>
  trackAnalyticsEvent("login", params);

export const trackSignUp = (params?: { method?: string; role?: string }) =>
  trackAnalyticsEvent("sign_up", params);

export const trackEmailVerified = (params?: { role?: string }) =>
  trackAnalyticsEvent("email_verified", params);

export const trackLogin2faVerified = (params?: { role?: string }) =>
  trackAnalyticsEvent("login_2fa_verified", params);

export const trackLogout = () => trackAnalyticsEvent("logout");

// The impersonation lifecycle itself must be reported even though every other
// event is muted while an admin impersonates someone.
export const trackAdminImpersonationStarted = (params?: {
  impersonated_user_role?: string;
}) =>
  trackAnalyticsEvent("admin_impersonation_started", params, {
    allowDuringImpersonation: true,
  });

export const trackAdminImpersonationStopped = (params?: { role?: string }) =>
  trackAnalyticsEvent("admin_impersonation_stopped", params, {
    allowDuringImpersonation: true,
  });

// Subscription & listing events
// The free plan is already active by default, so picking it starts nothing —
// and reporting it would make free sign-ups indistinguishable from paid ones in
// Google Ads. Only paid plans count as a subscription start.
export const trackSubscriptionStarted = (
  planKey: string,
  valueCents?: number,
) => {
  if (planKey === "free") return;

  trackAnalyticsEvent("subscription_started", {
    plan_key: planKey,
    value: valueCents ? valueCents / 100 : undefined,
    currency: "EUR",
  });
};

export const trackSubscriptionChanged = (planKey: string) =>
  trackAnalyticsEvent("subscription_changed", { plan_key: planKey });

export const trackSubscriptionCancelled = () =>
  trackAnalyticsEvent("subscription_cancelled");

export const trackListingCreated = (subject?: string, city?: string) =>
  trackAnalyticsEvent("listing_created", { subject, city });

// A recruited tutor only becomes operational when their first listing goes
// live. Unlike listing_created, this fires once per tutor and covers both
// paths (published straight away, or saved as a draft and published later),
// which is what a tutor-recruitment campaign should count.
export const trackFirstListingPublished = (subject?: string, city?: string) =>
  trackAnalyticsEvent("first_listing_published", { subject, city });

export const trackListingUpdated = () => trackAnalyticsEvent("listing_updated");

export const trackListingDeleted = () => trackAnalyticsEvent("listing_deleted");

export const trackConnectAccountCreated = () =>
  trackAnalyticsEvent("connect_account_created");

export const trackRequestCreated = (subject?: string) =>
  trackAnalyticsEvent("request_created", { subject });

export const trackRequestAccepted = () =>
  trackAnalyticsEvent("request_accepted");

export const trackRequestDeclined = () =>
  trackAnalyticsEvent("request_declined");

export const trackBookingConfirmed = () =>
  trackAnalyticsEvent("booking_confirmed");

export const trackPurchase = (value?: number, currency = "EUR") =>
  trackAnalyticsEvent("purchase", { value, currency });

export const trackPaymentMethodAdded = () =>
  trackAnalyticsEvent("payment_method_added");

export const trackStudentInviteSent = () =>
  trackAnalyticsEvent("student_invite_sent");

export const trackWaitlistSignup = (city?: string, subject?: string) =>
  trackAnalyticsEvent("waitlist_signup", { city, subject });

export const trackContactFormSubmitted = () =>
  trackAnalyticsEvent("contact_form_submitted");

export const trackProflinkerMatch = () =>
  trackAnalyticsEvent("proflinker_match");

// Demande de mise en relation (/demande) : conversion principale du site.
// L'event GA4 et la conversion Ads sont indépendants — l'un ou l'autre peut
// être configuré seul.
export const trackLeadFormSubmitted = (params?: {
  subject?: string;
  city?: string;
  level?: string;
  source?: string;
}) => trackAnalyticsEvent("lead_form_submitted", params);

export const trackLeadConversion = () => trackAdsConversion();

export const trackLeadCtaClicked = (location: string) =>
  trackAnalyticsEvent("lead_cta_clicked", { location });

// Activity events (page_view, search, etc.) - GA only; backend sent separately
type ActivityParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export const trackActivityAnalyticsEvent = (
  eventType: string,
  params?: ActivityParams,
) => trackAnalyticsEvent(`activity_${eventType}`, params);
